import { useState } from 'react'
import type { ModelUpgradeLimit } from '@/components/ui/image-model-picker'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/shadcn/dialog'
import { ImagePromptBox } from './common/image-prompt-box'
import { ImagePreviewContainer } from './common/image-preview-container'
import { useTheme } from '@/context/themeContext'
import { cn } from '@/lib/utils'
import { type ImageModel } from '@backend/lib/ai/functions/generateImage'
import { type GenerateImageOptions, type ImageResolution, getAvailableResolutions } from './types'

interface Props {
  isOpen: boolean
  onClose: () => void
  onGenerate: (
    prompt: string,
    model: ImageModel,
    options?: GenerateImageOptions,
  ) => Promise<{ url: string; enhancedPrompt?: string }>
  onUseImage: (imageUrl: string) => void
  isGenerating: boolean
  hasBrandColors?: boolean
  isFreePlan?: boolean
  onUpgradeClick?: (limit: ModelUpgradeLimit) => void
}

export function WorkspaceEditorPopupNew({
  isOpen,
  onClose,
  onGenerate,
  onUseImage,
  isGenerating,
  hasBrandColors,
  isFreePlan,
  onUpgradeClick,
}: Props) {
  const { theme } = useTheme()
  const [prompt, setPrompt] = useState('')
  const [generatedImageUrl, setGeneratedImageUrl] = useState<string>()
  const [enhancedPrompt, setEnhancedPrompt] = useState<string>()
  const [imageModel, setImageModel] = useState<ImageModel>('flux')
  const [referenceImages, setReferenceImages] = useState<string[]>([])
  const [flexSteps, setFlexSteps] = useState(50)
  const [flexGuidance, setFlexGuidance] = useState(5)
  const [useCaption, setUseCaption] = useState(true)
  const [useColorPalette, setUseColorPalette] = useState(true)
  const [resolution, setResolution] = useState<ImageResolution>('1K')
  const [enhance, setEnhance] = useState(true)

  if (!isOpen) return null

  const handleModelChange = (model: ImageModel) => {
    setImageModel(model)
    const available = getAvailableResolutions(model)
    if (!available.includes(resolution)) {
      setResolution(available[0]!)
    }
  }

  const handleGenerate = async () => {
    if (isGenerating) return

    try {
      const result = await onGenerate(prompt, imageModel, {
        referenceImages: referenceImages.length > 0 ? referenceImages : undefined,
        steps: flexSteps,
        guidance: flexGuidance,
        useCaption,
        useColorPalette,
        resolution,
        enhance,
      })
      setGeneratedImageUrl(result.url)
      setEnhancedPrompt(result.enhancedPrompt)
    } catch (error) {
      console.error('Failed to generate image:', error)
    }
  }

  const handleUseImage = () => {
    if (generatedImageUrl) {
      onUseImage(generatedImageUrl)
      handleClose()
    }
  }

  const handleClose = () => {
    if (!isGenerating) {
      setPrompt('')
      setGeneratedImageUrl(undefined)
      setEnhancedPrompt(undefined)
      setImageModel('flux')
      setReferenceImages([])
      setFlexSteps(50)
      setFlexGuidance(5)
      setUseCaption(true)
      setUseColorPalette(true)
      setResolution('1K')
      setEnhance(true)
      onClose()
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      if (generatedImageUrl) {
        handleUseImage()
      } else {
        handleGenerate()
      }
    }
  }

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent
        className={cn(
          'text-foreground-white flex h-[90vh] max-w-[750px] flex-col border-transparent bg-transparent',
          theme === 'dark' ? 'dark' : '',
        )}>
        <DialogHeader className="">
          <DialogTitle className="text-foreground-white">Generate Image</DialogTitle>
        </DialogHeader>

        <div className="flex min-h-0 flex-1 flex-col gap-4">
          <ImagePreviewContainer
            imageUrl={generatedImageUrl}
            isGenerating={isGenerating}
            placeholderText="Your generated image will appear here"
            enhancedPrompt={enhancedPrompt}
          />

          <ImagePromptBox
            prompt={prompt}
            onPromptChange={setPrompt}
            onKeyDown={handleKeyDown}
            placeholder="Describe the image you want to generate, or leave empty to auto-generate from your caption"
            isGenerating={isGenerating}
            hasGeneratedImage={!!generatedImageUrl}
            onCancel={handleClose}
            onGenerate={handleGenerate}
            onUseImage={handleUseImage}
            useImageButtonText="Accept"
            imageModel={imageModel}
            onModelChange={handleModelChange}
            referenceImages={referenceImages}
            onReferenceImagesChange={setReferenceImages}
            maxReferenceImages={8}
            flexSteps={flexSteps}
            onFlexStepsChange={setFlexSteps}
            flexGuidance={flexGuidance}
            onFlexGuidanceChange={setFlexGuidance}
            useCaption={useCaption}
            onUseCaptionChange={setUseCaption}
            useColorPalette={useColorPalette}
            onUseColorPaletteChange={setUseColorPalette}
            enhance={enhance}
            onEnhanceChange={setEnhance}
            showCaptionAndColors={true}
            hasBrandColors={hasBrandColors}
            resolution={resolution}
            onResolutionChange={setResolution}
            isFreePlan={isFreePlan}
            onUpgradeClick={onUpgradeClick}
          />
        </div>
      </DialogContent>
    </Dialog>
  )
}
